import React, { useState } from "react";
import { motion } from "framer-motion";
import { Copy, Check, ArrowRight, ArrowLeft, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";

const ease = [0.16, 1, 0.3, 1];
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.6, ease, delay },
});

const PROMPTS = [
  {
    num: "01",
    title: "Rewrite Your Professional Summary",
    tag: "Summary",
    prompt: `I'm applying for a [Job Title] position in [Industry], and I want my resume summary to stand out. Please rewrite this professional summary to make it more concise, engaging, and aligned with the expectations for this role. It should reflect my strengths, relevant skills, and years of experience in a compelling way.

[Paste your current summary here]`,
  },
  {
    num: "02",
    title: "Turn Responsibilities into Accomplishments",
    tag: "Bullet Points",
    prompt: `Here are the bullet points for one of my past roles. Please rework them to focus on measurable accomplishments rather than generic responsibilities. Make them results-oriented, use strong action verbs, and include numbers or metrics wherever possible.

[Paste your bullet points here]`,
  },
  {
    num: "03",
    title: "ATS Keyword Optimization",
    tag: "ATS",
    prompt: `I want my resume to pass ATS filters and still read well to human recruiters. Based on this job description [paste job description], can you help me optimize my resume content to include relevant keywords and phrases from the posting in a natural way?

[Paste your resume here]`,
  },
  {
    num: "04",
    title: "Career Change Positioning",
    tag: "Career Transition",
    prompt: `I'm transitioning into a new career from [Previous Field] to [New Field]. Please help me rewrite my resume summary and key achievements so they highlight transferable skills and make a strong case for why I'm a great fit, even without direct experience.

[Paste your current resume here]`,
  },
  {
    num: "05",
    title: "Full Resume Audit",
    tag: "Audit",
    prompt: `Can you audit this entire resume and point out areas where I'm being too vague, too wordy, or not showing enough impact? I want your feedback on tone, structure, and how I can better emphasize leadership, results, or innovation.

[Paste your resume here]`,
  },
  {
    num: "06",
    title: "Better Format & Layout",
    tag: "Structure",
    prompt: `Please suggest a better format or layout for my resume that emphasizes my most recent experience and key skills while de-emphasizing older, less relevant roles. I want it to look modern and be easy to scan in under 10 seconds.

[Describe your current layout or paste your resume here]`,
  },
  {
    num: "07",
    title: "Align Work History to a Target Role",
    tag: "Targeting",
    prompt: `Here is a job I'm targeting. Based on this description, can you help me rewrite or restructure my work history to better align with the core skills and qualifications they're looking for? Focus especially on aligning language and phrasing.

Job Description: [Paste JD here]
My Work History: [Paste your experience section here]`,
  },
  {
    num: "08",
    title: "Build a Strong Technical Skills Section",
    tag: "Skills",
    prompt: `I want to include a section on technical skills and tools, but I'm not sure what to list or how to format it. Based on my experience [brief description], can you help me write this section in a way that stands out to hiring managers?

[Brief description of your experience and tools used]`,
  },
  {
    num: "09",
    title: "Write a Killer Resume Headline",
    tag: "Headline",
    prompt: `Can you help me write a concise but powerful resume headline and subheadline that immediately tell the reader what type of role I'm seeking, my value proposition, and what makes me stand out?

[Brief background: current title, years of experience, target role]`,
  },
  {
    num: "10",
    title: "Think Like a Hiring Manager",
    tag: "Final Review",
    prompt: `Please act like a hiring manager in [Industry/Role]. Based on this resume, what would make you more likely to invite me for an interview? What should I change, cut, or add to improve my chances?

[Paste your full resume here]`,
  },
];

const PromptCard = ({ item, index }) => {
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(item.prompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-30px" }}
      transition={{ duration: 0.5, ease, delay: (index % 3) * 0.07 }}
      data-testid={`prompt-card-${index + 1}`}
      className="bg-white rounded-3xl border border-black/5 shadow-sm overflow-hidden hover:shadow-[0_8px_30px_rgba(255,82,0,0.08)] transition-all duration-300"
    >
      <div className="p-7">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <span className="text-3xl font-medium text-brand/20">{item.num}</span>
            <span className="px-3 py-1 rounded-full bg-brand-light text-brand text-xs font-medium">{item.tag}</span>
          </div>
          <button onClick={handleCopy} data-testid={`copy-prompt-${index + 1}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all flex-shrink-0 ${copied ? "bg-green-500 text-white" : "bg-surface-raised text-ink hover:bg-brand hover:text-white"}`}>
            {copied ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
          </button>
        </div>
        <h3 className="text-ink font-medium text-base mb-3">{item.title}</h3>
        <button onClick={() => setOpen(!open)} className="text-brand text-xs font-medium hover:text-brand-hover transition-colors">
          {open ? "Hide prompt ↑" : "Preview prompt ↓"}
        </button>
      </div>
      {open && (
        <div className="bg-ink px-6 pb-6 pt-1">
          <pre className="text-green-300/80 text-xs font-mono leading-relaxed whitespace-pre-wrap">{item.prompt}</pre>
        </div>
      )}
    </motion.div>
  );
};

export default function BlogPost10PromptsPage() {
  return (
    <div data-testid="blog-post-page" className="bg-white">
      <SEOHead
        title="10 Prompts I Used to Fix My Resume | ApplyStrat Blog"
        description="Copy these 10 AI prompts to rewrite your resume summary, optimize for ATS, turn responsibilities into accomplishments, and stand out to hiring managers."
        canonical="https://applystrat.com/blog/10-prompts-fix-resume"
      />

      {/* ── HERO ── */}
      <section className="pt-36 pb-12 text-center" style={{ background: "radial-gradient(circle at 50% -20%, rgba(255,82,0,0.10) 0%, transparent 60%)" }}>
        <div className="max-w-3xl mx-auto px-6">
          <motion.div {...fadeUp()}>
            <Link to="/blog" className="inline-flex items-center gap-1.5 text-ink-secondary text-sm hover:text-ink transition-colors mb-8">
              <ArrowLeft size={14} /> Back to Blog
            </Link>
            <div className="flex items-center justify-center gap-3 mb-6">
              <span className="px-3 py-1 rounded-full bg-brand-light text-brand text-xs font-medium">Resume Tips</span>
              <span className="text-ink-secondary text-xs">5 min read</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-medium tracking-tighter text-ink mb-5 leading-tight">
              10 Prompts I Used to<br /><span className="text-brand">Fix My Resume</span>
            </h1>
            <p className="text-lg text-ink-secondary max-w-xl mx-auto leading-relaxed">
              Copy-paste these into ChatGPT or Claude. Each one tackles a specific resume problem — summaries, bullets, ATS gaps, targeting, and more.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── INTRO ── */}
      <section className="py-12 bg-white">
        <div className="max-w-2xl mx-auto px-6">
          <motion.div {...fadeUp()} className="prose prose-sm max-w-none">
            <p className="text-ink-secondary leading-relaxed text-base">
              Most job seekers know their resume could be better — they just don't know exactly where to start. These 10 prompts are designed to work independently: pick the one that matches your biggest gap right now. Each prompt tells the AI exactly what to do, what structure to follow, and what result to produce.
            </p>
            <p className="text-ink-secondary leading-relaxed text-base mt-4">
              For a complete end-to-end audit and full rewrite, use our{" "}
              <Link to="/resume-optimizer" className="text-brand hover:text-brand-hover font-medium transition-colors">
                Master AI Resume Prompt
              </Link>{" "}
              instead.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── PROMPTS GRID ── */}
      <section className="py-12 pb-24 bg-surface-raised" data-testid="prompts-grid">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {PROMPTS.map((item, i) => (
              <PromptCard key={item.num} item={item} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ── BOTTOM CTA ── */}
      <section className="py-20 bg-white" data-testid="blog-post-cta">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div {...fadeUp()}>
            <h2 className="text-3xl font-medium text-ink mb-4">Want the Full 9-Step Rewrite?</h2>
            <p className="text-ink-secondary text-base mb-8 leading-relaxed">
              The Master Prompt runs a complete diagnostic — ATS audit, language rewrite, skills restructure, and final polished resume. One prompt, everything fixed.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild size="lg" className="shadow-sm">
                <Link to="/resume-optimizer" data-testid="cta-optimizer">
                  Use the Master Prompt <ArrowRight size={16} />
                </Link>
              </Button>
              <Button asChild variant="secondary">
                <Link to="/apply" data-testid="cta-apply">Work With ApplyStrat</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
